/**
 * Authentication Utilities with Refresh Token Support
 * 
 * Features:
 * - Token storage (localStorage for refresh, memory for access)
 * - Automatic token refresh on 401
 * - Axios interceptor for seamless auth
 * - Token expiry detection
 */

import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Token storage keys
const REFRESH_TOKEN_KEY = 'eros_refresh_token';
const ACCESS_TOKEN_KEY = 'eros_access_token';
const USER_KEY = 'eros_user';
// Cross-tab refresh coordination
const REFRESH_LOCK_KEY = 'eros_refresh_lock';   // writer tab announces "I'm refreshing"
const REFRESH_LOCK_TTL_MS = 5_000;              // stale lock after this
const _TAB_ID = Math.random().toString(36).slice(2, 10);

// In-memory access token (also backed by localStorage)
let accessToken = null;
let refreshPromise = null;

/**
 * Store tokens after login/register
 */
export const storeTokens = (accessTokenValue, refreshToken, user) => {
  accessToken = accessTokenValue;
  localStorage.setItem(ACCESS_TOKEN_KEY, accessTokenValue);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  if (user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  }
  // Bust /auth/me + /auth/tc-status caches — the new token belongs to
  // (potentially) a different user, so stale data must be discarded.
  try { invalidateMeCache(); } catch { /* invalidateMeCache defined below */ }
};

/**
 * Get stored access token
 */
export const getAccessToken = () => {
  if (accessToken) return accessToken;
  const stored = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (stored) {
    accessToken = stored; // Restore to memory
  }
  return stored;
};

/**
 * Get stored refresh token
 */
export const getRefreshToken = () => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Get stored user
 */
export const getStoredUser = () => {
  const userStr = localStorage.getItem(USER_KEY);
  return userStr ? JSON.parse(userStr) : null;
};

/**
 * Clear all auth data
 */
export const clearTokens = () => {
  accessToken = null;
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  try { invalidateMeCache(); } catch { /* invalidateMeCache defined below */ }
};

/**
 * Check if user is potentially logged in (has refresh token)
 */
export const hasRefreshToken = () => {
  return !!getRefreshToken();
};

/**
 * Refresh the access token using the refresh token
 * Returns the new access token or null if refresh fails
 *
 * Cross-tab coordination: only ONE tab at a time performs the actual
 * network call. Other tabs wait for `storage` events on ACCESS_TOKEN_KEY
 * to pick up the freshly-rotated token. This eliminates the multi-tab
 * race that was tripping the backend's reuse-detection (269 events
 * observed pre-fix on the admin user alone).
 */
export const refreshAccessToken = async () => {
  const refreshToken = getRefreshToken();

  if (!refreshToken) {
    return null;
  }

  // Same-tab de-dupe
  if (refreshPromise) {
    return refreshPromise;
  }

  // Cross-tab de-dupe: if another tab is already refreshing (fresh lock),
  // wait for it to write a new access token to localStorage and use that.
  const existingLock = _readLock();
  if (existingLock && existingLock.tab !== _TAB_ID) {
    console.debug(`[auth] tab ${_TAB_ID} waiting on refresh from tab ${existingLock.tab}`);
    const cross = await _waitForCrossTabRefresh(refreshToken);
    if (cross) return cross;
    // If cross-tab refresh didn't produce a fresh token (stale lock,
    // crashed tab, other tab errored) fall through and try ourselves.
  }

  // Take the lock and refresh
  _writeLock();
  refreshPromise = (async () => {
    try {
      const response = await axios.post(
        `${API}/auth/refresh`,
        { refresh_token: refreshToken }
      );

      const { access_token, refresh_token: newRefreshToken, user } = response.data;
      if (!access_token || !newRefreshToken) {
        console.error('[auth] refresh returned incomplete payload — keeping existing tokens');
        return null;
      }
      // Store new tokens (this also fires storage events for other tabs)
      storeTokens(access_token, newRefreshToken, user);
      return access_token;
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const detailCode = typeof detail === 'object' ? detail?.code : null;

      // Backend grace-period race: another tab just rotated us. Wait a
      // short moment for the new token to land in localStorage and use it.
      if (status === 401 && detailCode === 'token_recently_rotated') {
        const wait = detail?.retry_after_ms || 500;
        console.debug(`[auth] token_recently_rotated race — waiting ${wait}ms for sibling tab`);
        const cross = await _waitForCrossTabRefresh(refreshToken, wait + 500);
        if (cross) return cross;
        // If we still don't have a new token after the wait, DO NOT clear —
        // this is a race, not an auth failure. Caller can retry.
        return null;
      }

      console.error('[auth] token refresh failed:', status, detail);
      // Only clear tokens on genuine auth failures (400/401/403 that are
      // NOT the recently-rotated race handled above). Do NOT drop the
      // session on 429 or network hiccups.
      if (status && status !== 429 && (status === 400 || status === 401 || status === 403)) {
        clearTokens();
      }
      return null;
    } finally {
      refreshPromise = null;
      _clearLock();
    }
  })();

  return refreshPromise;
};

// ─── Cross-tab refresh helpers ───────────────────────────────────────
function _readLock() {
  try {
    const raw = localStorage.getItem(REFRESH_LOCK_KEY);
    if (!raw) return null;
    const lock = JSON.parse(raw);
    if (!lock?.at || (Date.now() - lock.at) > REFRESH_LOCK_TTL_MS) {
      return null; // stale — treat as no lock
    }
    return lock;
  } catch { return null; }
}
function _writeLock() {
  try { localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify({ tab: _TAB_ID, at: Date.now() })); } catch { /* ignore */ }
}
function _clearLock() {
  try {
    const lock = _readLock();
    if (!lock || lock.tab === _TAB_ID) localStorage.removeItem(REFRESH_LOCK_KEY);
  } catch { /* ignore */ }
}

/**
 * Wait for another tab to complete a refresh. Returns the new access
 * token if the sibling tab writes one to localStorage within `timeoutMs`,
 * otherwise null. Detects new tokens via storage events (immediate) with
 * a polling fallback for the same-tab case.
 */
function _waitForCrossTabRefresh(oldRefreshToken, timeoutMs = REFRESH_LOCK_TTL_MS) {
  return new Promise((resolve) => {
    const start = Date.now();
    let done = false;
    const finish = (val) => { if (!done) { done = true; window.removeEventListener('storage', onStorage); clearInterval(poll); resolve(val); } };

    const onStorage = (e) => {
      if (e.key === ACCESS_TOKEN_KEY && e.newValue && e.newValue !== oldRefreshToken) {
        // In-memory copy needs sync too
        accessToken = e.newValue;
        finish(e.newValue);
      }
      if (e.key === REFRESH_TOKEN_KEY && e.newValue && e.newValue !== oldRefreshToken) {
        // Refresh token rotated — read the new access token
        const at = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (at) { accessToken = at; finish(at); }
      }
    };
    window.addEventListener('storage', onStorage);

    // Polling fallback (storage event doesn't fire in the tab that wrote)
    const poll = setInterval(() => {
      const curRefresh = getRefreshToken();
      if (curRefresh && curRefresh !== oldRefreshToken) {
        const at = localStorage.getItem(ACCESS_TOKEN_KEY);
        if (at) { accessToken = at; finish(at); }
      }
      if (Date.now() - start > timeoutMs) finish(null);
    }, 100);
  });
}

/**
 * Login and store tokens
 */
export const login = async (email, password, deviceFp = null) => {
  const payload = { email, password };
  if (deviceFp) payload.device_fp = deviceFp;
  const response = await axios.post(
    `${API}/auth/login`,
    payload
  );
  
  const { access_token, refresh_token, user } = response.data;
  storeTokens(access_token, refresh_token, user);
  
  return response.data;
};

/**
 * Fraud Guardrails Phase 2 (A+C) — fire-and-forget report of the current
 * device fingerprint on app boot, so existing users get progressively
 * enrolled into their device_fps history array.
 */
export const reportDeviceFingerprint = async (deviceFp) => {
  if (!deviceFp) return;
  try {
    await authAxios.post("/auth/device-fingerprint", { device_fp: deviceFp });
  } catch {
    /* best-effort — never blocks the app */
  }
};

/**
 * Register and store tokens
 */
export const register = async (name, email, password, referralCode = null, deviceFp = null) => {
  const payload = { name, email, password };
  if (referralCode) payload.referral_code = referralCode;
  if (deviceFp) payload.device_fp = deviceFp;
  
  const response = await axios.post(
    `${API}/auth/register`,
    payload
  );
  
  const { access_token, refresh_token, user } = response.data;
  storeTokens(access_token, refresh_token, user);
  
  return response.data;
};

/**
 * Logout - clear tokens and optionally revoke on server
 */
export const logout = async () => {
  const refreshToken = getRefreshToken();
  
  try {
    if (refreshToken) {
      await axios.post(
        `${API}/auth/logout`,
        { refresh_token: refreshToken }
      );
    }
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    clearTokens();
  }
};

/**
 * Logout from all devices
 */
export const logoutAll = async () => {
  try {
    const token = getAccessToken();
    if (token) {
      await axios.post(
        `${API}/auth/logout-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }
  } catch (error) {
    console.error('Logout all error:', error);
  } finally {
    clearTokens();
  }
};

/**
 * Get active devices/sessions
 */
export const getDevices = async () => {
  const token = getAccessToken();
  const response = await axios.get(
    `${API}/auth/devices`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

/**
 * Revoke a specific device
 */
export const revokeDevice = async (deviceId) => {
  const token = getAccessToken();
  await axios.post(
    `${API}/auth/revoke/${deviceId}`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  );
};

/**
 * Change password (revokes all sessions)
 */
export const changePassword = async (currentPassword, newPassword) => {
  const token = getAccessToken();
  const response = await axios.post(
    `${API}/auth/change-password`,
    { current_password: currentPassword, new_password: newPassword },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  // Clear tokens since all sessions are revoked
  clearTokens();
  return response.data;
};

/**
 * Create an axios instance with automatic token refresh
 */
export const createAuthenticatedAxios = () => {
  const instance = axios.create({
    baseURL: API,
  });
  
  // Request interceptor - add auth header
  instance.interceptors.request.use(
    async (config) => {
      let token = getAccessToken();
      // If no access token but we have a refresh token, try refreshing first
      if (!token) {
        const refreshToken = getRefreshToken();
        if (refreshToken) {
          token = await refreshAccessToken();
        }
      }
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
  
  // Response interceptor - handle 401 and refresh
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Handle rate limit — DO NOT drop tokens or trigger a session logout,
      // that only makes the storm worse. Let the caller retry / show the
      // "please slow down" toast if it wants to.
      if (error.response?.status === 429) {
        return Promise.reject(error);
      }

      // ─── Global 402 OUT_OF_COINS gate ────────────────────────────────
      // Any endpoint that returns 402 with `detail.code === "OUT_OF_COINS"`
      // (or a top-level `code === "OUT_OF_COINS"`) triggers the shared
      // "Out of Coins" modal via the CoinGateProvider window listener.
      // This decouples the modal from per-caller code — no need for each
      // gated action to wrap itself in requireCoins() to catch a race
      // where the backend rejects between the pre-check and the LLM call.
      if (error.response?.status === 402) {
        const data = error.response?.data || {};
        const detail = data.detail;
        const code =
          (typeof detail === "object" && detail?.code) ||
          data.code ||
          (typeof detail === "string" ? detail : null);
        if (code === "OUT_OF_COINS") {
          try {
            window.dispatchEvent(
              new CustomEvent("eros:out-of-coins", { detail: data })
            );
          } catch { /* SSR / test env — safe to swallow */ }
        }
      }

      const originalRequest = error.config;
      
      // If 401 and not already retrying
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        
        // Try to refresh token
        const newToken = await refreshAccessToken();
        
        if (newToken) {
          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        }
        // Refresh failed — don't hard-redirect here, let the caller handle it
        return Promise.reject(error);
      }
      
      return Promise.reject(error);
    }
  );
  
  return instance;
};

// Create default authenticated axios instance
export const authAxios = createAuthenticatedAxios();

// ─── /auth/me & /auth/tc-status short-lived cache ───
// Prior to Feb 2026 the ProtectedRoute effect re-hit these on EVERY
// route change (`location.pathname` dep), so a single session could
// blow through the 100/min rate limit just navigating. Cache is
// keyed on the current access-token prefix so a login/refresh
// invalidates it automatically. TTL is short enough that user data
// still stays fresh but long enough to smooth out route-change bursts.
const _ME_TTL_MS = 30_000;
const _TC_TTL_MS = 60_000;
let _meCache = { at: 0, key: "", data: null };
let _tcCache = { at: 0, key: "", data: null };

function _tokenKey() {
  const t = getAccessToken() || "";
  return t.slice(-24);
}

export const getMe = async ({ force = false } = {}) => {
  const key = _tokenKey();
  if (!force && _meCache.data && _meCache.key === key && (Date.now() - _meCache.at) < _ME_TTL_MS) {
    return _meCache.data;
  }
  const response = await authAxios.get("/auth/me");
  _meCache = { at: Date.now(), key, data: response.data };
  return response.data;
};

export const getTcStatus = async ({ force = false } = {}) => {
  const key = _tokenKey();
  if (!force && _tcCache.data && _tcCache.key === key && (Date.now() - _tcCache.at) < _TC_TTL_MS) {
    return _tcCache.data;
  }
  const response = await authAxios.get("/auth/tc-status");
  _tcCache = { at: Date.now(), key, data: response.data };
  return response.data;
};

export const invalidateMeCache = () => {
  _meCache = { at: 0, key: "", data: null };
  _tcCache = { at: 0, key: "", data: null };
};

/**
 * Initialize auth on app load
 * Tries to restore session from refresh token
 */
export const initializeAuth = async () => {
  const refreshToken = getRefreshToken();
  
  if (!refreshToken) {
    return null;
  }
  
  // Try to get a new access token
  const token = await refreshAccessToken();
  
  if (token) {
    // Fetch user data
    try {
      const response = await authAxios.get('/auth/me');
      const user = response.data;
      localStorage.setItem(USER_KEY, JSON.stringify(user));
      return user;
    } catch (error) {
      console.error('Failed to fetch user:', error);
      clearTokens();
      return null;
    }
  }
  
  return null;
};

export default {
  storeTokens,
  getAccessToken,
  getRefreshToken,
  getStoredUser,
  clearTokens,
  hasRefreshToken,
  refreshAccessToken,
  login,
  register,
  logout,
  logoutAll,
  getDevices,
  revokeDevice,
  changePassword,
  authAxios,
  initializeAuth,
};
